"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ChevronRight, Gauge, Layers, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
export default function BlendUniformity() {
  const shouldReduceMotion = useReducedMotion();
  const specifications = [{
    label: "Sampling Speed",
    value: "Continuous real-time"
  }, {
    label: "Technique",
    value: "NIR / Raman Optical"
  }, {
    label: "Compliance",
    value: "GMP & data integrity support"
  }];
  return <section id="blenduniformityhero" data-nav="dark" className="relative overflow-hidden bg-sidebar text-sidebar-foreground py-16 md:py-24 lg:py-28">
      {/* Background Subtle Geometric Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
          <defs>
            <pattern id="navy-hero-grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="currentColor" strokeWidth="0.75" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#navy-hero-grid)" />
        </svg>
      </div>

      {/* Decorative Radial Glows */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-sidebar-primary/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-sidebar-accent/30 blur-3xl pointer-events-none" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column (8 cols in 12-grid layout) */}
          <motion.div className="lg:col-span-8 flex flex-col items-start" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            {/* Breadcrumb Eyebrow */}
            <div className="mb-6">
              <Breadcrumb>
                <BreadcrumbList className="text-sidebar-foreground/75 text-xs sm:text-sm">
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/SolutionsPage" className="text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors">
                        Solutions
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-sidebar-foreground/50">
                    <ChevronRight className="size-3.5" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/SolutionsPage" className="text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors">
                        Process Analytical Technology
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-sidebar-foreground/50">
                    <ChevronRight className="size-3.5" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-sidebar-primary-foreground font-semibold">
                      Blend Uniformity
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight text-sidebar-foreground leading-tight sm:leading-tight mb-6">
              Verifying Blend Homogeneity In-Line During Powder Blending
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg text-sidebar-foreground/85 max-w-2xl leading-relaxed mb-8">
              Eliminate thief sampling errors and reduce cycle validation times.
              Our non-destructive optical spectroscopy probes deliver immediate, continuous
              RSD quantification directly inside operational blending vessels.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 mb-10">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-semibold px-6 shadow-md">
                <Link to="/ContactPage">
                  Discuss Your Application
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="bg-sidebar-accent border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent/80 rounded-md px-6 font-medium">
                <Link to="/SolutionsPage">
                  Explore PAT Technologies
                </Link>
              </Button>
            </div>

            {/* Quick Spec Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-2xl pt-6 border-t border-sidebar-border">
              {specifications.map((item, i) => <div key={item.label} data-index={i} className="flex flex-col bg-sidebar-accent/50 rounded-lg p-3 border border-sidebar-border/60">
                  <span className="text-xs text-sidebar-foreground/70 font-medium">
                    {item.label}
                  </span>
                  <span className="text-sm font-semibold text-sidebar-foreground mt-0.5">
                    {item.value}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column (4 cols in 12-grid layout) - Technical Illustration */}
          <motion.div className="lg:col-span-4 w-full flex justify-center" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.15,
          ease: "easeOut"
        }}>
            <div className="w-full max-w-md bg-sidebar-accent/40 backdrop-blur-md rounded-2xl p-6 border border-sidebar-border shadow-xl relative overflow-hidden">
              {/* Header Label */}
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-sidebar-border">
                <div className="flex items-center gap-2">
                  <Layers className="size-4 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-sidebar-foreground">
                    Probe In-Situ Setup
                  </span>
                </div>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/20 text-primary border border-primary/30">
                  <span className="size-1.5 rounded-full bg-primary animate-pulse" />
                  Active Scan
                </span>
              </div>

              {/* Technical SVG Schematic */}
              <div className="relative aspect-4/3 w-full bg-sidebar/80 rounded-xl p-4 flex items-center justify-center border border-sidebar-border overflow-hidden">
                <svg viewBox="0 0 320 240" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Vessel Wall Outline (Bin / Blender Cone) */}
                  <path d="M 40 40 L 90 200 L 230 200 L 280 40" stroke="currentColor" strokeWidth="2.5" strokeDasharray="4 3" className="text-sidebar-foreground/30" />
                  <path d="M 30 40 L 290 40" stroke="currentColor" strokeWidth="2" className="text-sidebar-foreground/40" />

                  {/* Powder Flow Lines */}
                  <path d="M 80 100 Q 160 140 240 100" stroke="currentColor" strokeWidth="1.5" className="text-sidebar-foreground/20" />
                  <path d="M 95 130 Q 160 165 225 130" stroke="currentColor" strokeWidth="1.5" className="text-sidebar-foreground/20" />
                  <path d="M 110 160 Q 160 180 210 160" stroke="currentColor" strokeWidth="1.5" className="text-sidebar-foreground/20" />

                  {/* Powder Matrix Particles */}
                  {[{
                  cx: 75,
                  cy: 75,
                  r: 2.5
                }, {
                  cx: 120,
                  cy: 90,
                  r: 2
                }, {
                  cx: 200,
                  cy: 80,
                  r: 3
                }, {
                  cx: 245,
                  cy: 110,
                  r: 2
                }, {
                  cx: 100,
                  cy: 150,
                  r: 3
                }, {
                  cx: 215,
                  cy: 160,
                  r: 2.5
                }, {
                  cx: 140,
                  cy: 185,
                  r: 2
                }, {
                  cx: 180,
                  cy: 180,
                  r: 2.5
                }].map((dot, index) => <circle key={`particle-${index}`} data-index={index} cx={dot.cx} cy={dot.cy} r={dot.r} className="text-primary/70 fill-current" />)}

                  {/* Optical Probe Body */}
                  <rect x="148" y="10" width="24" height="105" rx="3" className="fill-sidebar-accent stroke-sidebar-border" strokeWidth="1.5" />
                  {/* Flange Mount */}
                  <rect x="138" y="34" width="44" height="8" rx="2" className="fill-sidebar-border stroke-sidebar-foreground/40" strokeWidth="1" />

                  {/* Sapphire Optical Window */}
                  <rect x="152" y="110" width="16" height="8" rx="2" className="fill-primary/40 stroke-primary" strokeWidth="1.5" />

                  {/* Optical Beam Projection */}
                  <path d="M 152 118 L 115 175 L 205 175 Z" className="fill-primary/15" />
                  <line x1="160" y1="118" x2="160" y2="175" className="stroke-primary" strokeWidth="1.5" strokeDasharray="3 2" />

                  {/* Focus Spot */}
                  <circle cx="160" cy="148" r="8" className="fill-primary/20 stroke-primary" strokeWidth="1" />
                  <circle cx="160" cy="148" r="2.5" className="fill-primary" />
                </svg>

                {/* Floating Real-time HUD pill */}
                <div className="absolute bottom-3 right-3 bg-sidebar/95 border border-sidebar-border/80 shadow-md rounded-md px-2.5 py-1 text-[11px] text-sidebar-foreground flex items-center gap-1.5">
                  <Gauge className="size-3 text-primary" />
                  <span>RSD: <strong>1.42%</strong></span>
                </div>
              </div>

              {/* Technical Caption */}
              <div className="mt-4 flex items-start gap-2 text-xs text-sidebar-foreground/80">
                <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                <p>
                  Immersible sapphire window optical interface engineered for sanitary tri-clamp vessel ports.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}